import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Button } from '../../components';
import ProgressBar from 'components/ProgressBar/progressBar';
import QuestionAndOptionsComponent from './QuestionAndOptions';
import YesNoModal from 'components/Modals/yesNoModal';
import InformationModal from 'components/Modals/informationModal';
import ReportQuestionModal from 'components/Modals/reportQuestion';
import * as actions from './actions';
import { useActions } from 'hooks/useActions';
import {
  TestContainer,
  TestName,
  QuestionAndOptionsContainer,
  TimerAndQuestionsContainer,
  QuestionComponent,
  QuestionsBoxContainer,
  QuestionsSelectionButton,
  TimerContainer,
  TimerTextLabel,
  QuestionsSelectionContainer,
  TopHeadingContainer,
  SubmitAndInfoButtonContainer,
  SubmissionContainer,
  SubmissionText,
  SubmitTestLink,
  ItalicText,
} from './style';

export default function MultichoiceTestPage() {
  let screenWidth = window.innerWidth;
  let isSmallDevice = screenWidth < 992;
  let { ticketId } = useParams();
  const [isAdaptiveTest, setIsAdaptiveTest] = useState(true);
  const [timeLeft, setTimeLeft] = useState(0);
  const [timeLeftText, setTimeLeftText] = useState('');
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showInformationModal, setShowInformationModal] = useState(false);
  const [modalInfo, setModalInfo] = useState({});
  const [showReportQuestionModal, setShowReportQuestionModal] = useState(false);
  const [autoSubmitIntervalId, setAutoSubmitIntervalId] = useState(undefined);
  const history = useHistory();
  let inactiveTimeRef = useRef(0);

  const { loadTicket, closeTicket, reportQuestion, setCurrentQuestionObject } = useActions(actions);

  const {
    ticketResponse: { ticket, test },
    closedTicket,
    error,
    reportedQuestion,
    questionsAttempted,
    currentQuestionObject,
    currentOptionId,
    nonAdaptiveQuestionObject,
  } = useSelector((state) => state.multichoiceTestPage);

  useEffect(() => {
    loadTicket(ticketId);
  }, []);

  useEffect(() => {
    if (ticket && test) {
      if (ticket.submitted || ticket.archived) history.push('/tickets');
      setIsAdaptiveTest(test.adaptiveTest);
      updateTimer(ticket);
    }
  }, [ticket, test]);

  useEffect(() => {
    if (error) toast.error(error);
    if (error === 'The ticket has already been submitted') history.push('/tickets');
  }, [error]);

  useEffect(() => {
    if (!ticketId || closedTicket._id === ticketId) history.push('/tickets');
  }, [closedTicket, ticketId]);

  const reportQuestionFeedback = (data) => {
    setShowReportQuestionModal(false);
    if (data.isSubmitted) {
      delete data.isSubmitted;
      data.test = test._id;
      data.question = currentQuestionObject.question._id;
      reportQuestion(data);
    }
  };

  useEffect(() => {
    if (reportedQuestion._id) {
      toast.success('Feedback submitted successfully');
    }
  }, [reportedQuestion]);

  const updateTimer = (ticket) => {
    if (!ticket._id) return;
    let endTime = new Date().setMilliseconds(ticket.timeRemaining);
    if (autoSubmitIntervalId) {
      clearInterval(autoSubmitIntervalId);
    }
    const intervalId = setInterval(() => {
      const timeRemainingInMinutes = (endTime - new Date()) / 60000;
      if (timeRemainingInMinutes <= 0) {
        clearInterval(intervalId);
        clearInterval(autoSubmitIntervalId);
        if (intervalId === autoSubmitIntervalId) {
          return submitTicket(true);
        }
      }
      setTimeLeft(timeRemainingInMinutes);
      let timeLeftText = '';
      if (timeRemainingInMinutes <= 1) {
        timeLeftText = Math.ceil((endTime - new Date()) / 1000) + ' seconds';
      } else {
        timeLeftText = Math.ceil(timeRemainingInMinutes) + ' minutes';
      }
      setTimeLeftText(timeLeftText);
    }, 1000);
    setAutoSubmitIntervalId(intervalId);
  };

  const getQuestionsDonePercentage = () => {
    return (questionsAttempted / ticket.totalQuestions) * 100;
  };

  const getTimeLeftPercentage = () => {
    return ticket.timeLimit && timeLeft ? ((ticket.timeLimit - timeLeft) / ticket.timeLimit) * 100 : 0;
  };

  const getProgressBarType = () => {
    if (getQuestionsDonePercentage() - getTimeLeftPercentage() >= 0) {
      return 'success';
    } else if (getTimeLeftPercentage() - getQuestionsDonePercentage() < 10) {
      return 'warning';
    } else {
      return 'danger';
    }
  };

  const submitTicket = (isConfirmed) => {
    setShowConfirmationModal(false);
    if (isConfirmed) {
      let params = {
        question: currentQuestionObject ? currentQuestionObject.question._id : '',
        option: currentOptionId,
      };
      if (isAdaptiveTest && inactiveTimeRef.current) params.inactiveTime = inactiveTimeRef.current;
      closeTicket(ticketId, params);
    }
  };

  const confirmSubmit = () => {
    let message = 'Would you like to submit the test?';
    let questionsAnswered = isAdaptiveTest ? questionsAttempted + (currentOptionId ? 1 : 0) : questionsAttempted;
    if (questionsAnswered < ticket.totalQuestions) {
      message = 'It looks like you have not answered all of the questions. Would you like to submit your test anyway?';
    } else if (!isAdaptiveTest) {
      message = `You still have ${timeLeftText} remaining to revise your answers. If you submit the test, then you will not be allowed to make any changes. Would you like to submit your test anyway?`;
    }
    setModalInfo({
      title: 'Confirmation',
      body: message,
    });
    setShowConfirmationModal(true);
  };

  const getQuestionsSelectionBox = () => (
    <QuestionsSelectionContainer>
      {Object.values(nonAdaptiveQuestionObject).map((value, index) => {
        return (
          <QuestionsSelectionButton
            key={value.question._id}
            isAnswered={value.question.isAnswered}
            isActive={
              currentQuestionObject &&
              currentQuestionObject.question &&
              value.question._id === currentQuestionObject.question._id
            }
            onClick={() => setCurrentQuestionObject(value)}
          >
            {index + 1}
          </QuestionsSelectionButton>
        );
      })}
    </QuestionsSelectionContainer>
  );

  const displayTimerSection = () => {
    return (
      <TimerContainer>
        <TimerTextLabel isAdaptiveTest={isAdaptiveTest || isSmallDevice}>
          <ItalicText>{questionsAttempted} </ItalicText> /<ItalicText>{ticket.totalQuestions}</ItalicText> answered
        </TimerTextLabel>
        <ProgressBar percent={getQuestionsDonePercentage()} barType="track" />
        <TimerTextLabel isAdaptiveTest={isAdaptiveTest || isSmallDevice}>
          {timeLeftText ? `${timeLeftText} left of ${ticket.timeLimit}` : 'Calculating...'}
        </TimerTextLabel>
        <ProgressBar percent={getTimeLeftPercentage()} barType={getProgressBarType()} />
      </TimerContainer>
    );
  };

  const setInactiveTime = (time) => (inactiveTimeRef.current = time);

  return (
    <TestContainer>
      {showConfirmationModal && (
        <YesNoModal modalInfo={modalInfo} confirm={(isConfirmed) => submitTicket(isConfirmed)} />
      )}
      {showInformationModal && (
        <InformationModal
          title={test.name}
          textHTML={test.candidateDescription}
          closeModal={() => setShowInformationModal(false)}
        />
      )}
      {showReportQuestionModal && (
        <ReportQuestionModal modalInfo={modalInfo} confirm={(data) => reportQuestionFeedback(data)} />
      )}

      <TopHeadingContainer>
        <TestName>{test.name}</TestName>
        <Button primaryBlue primaryGreenBorder removeMargin buttonClick={() => setShowReportQuestionModal(true)}>
          <i className="fa fa-flag">&nbsp;</i> {screenWidth > 600 ? 'Report question' : ''}
        </Button>
      </TopHeadingContainer>

      {(isAdaptiveTest || isSmallDevice) && displayTimerSection()}
      {ticket._id && (
        <QuestionAndOptionsContainer>
          <QuestionComponent isAdaptiveTest={isAdaptiveTest}>
            {!isAdaptiveTest && !currentQuestionObject ? (
              <SubmissionContainer>
                <SubmissionText>
                  Thank you for answering the questions. Feel free to change your answers until the deadline has passed.
                  <br />
                  <SubmitTestLink onClick={() => confirmSubmit()}>Submit Test</SubmitTestLink>
                </SubmissionText>
              </SubmissionContainer>
            ) : (
              <QuestionAndOptionsComponent setInactiveTime={setInactiveTime} />
            )}
          </QuestionComponent>

          {isAdaptiveTest ? (
            <SubmitAndInfoButtonContainer isAdaptiveTest>
              <Button
                title="Show description"
                removeMargin
                primaryBlue
                primaryGreenBorder
                buttonClick={() => setShowInformationModal(true)}
              >
                <i className="fa fa-info">&nbsp;</i>Info
              </Button>
              <Button
                removeMargin
                usePadding="0 10px 0 15px"
                primaryBlue
                primaryGreenBorder
                title="Submit this test"
                buttonClick={() => confirmSubmit()}
              >
                <i className="fa fa-paper-plane-o">&nbsp;</i>Submit
              </Button>
            </SubmitAndInfoButtonContainer>
          ) : (
            <TimerAndQuestionsContainer>
              <QuestionsBoxContainer>
                {!isSmallDevice && displayTimerSection()}
                {getQuestionsSelectionBox()}
                <SubmitAndInfoButtonContainer isAdaptiveTest>
                  <Button
                    width={50}
                    textAlign="left"
                    title="Show description"
                    secondary
                    buttonClick={() => setShowInformationModal(true)}
                  >
                    <i className="fa fa-info">&nbsp;</i>Info
                  </Button>
                  <Button
                    width={50}
                    textAlign="right"
                    title="Submit this test"
                    secondary
                    buttonClick={() => confirmSubmit()}
                  >
                    <i className="fa fa-paper-plane-o">&nbsp;</i>Submit
                  </Button>
                </SubmitAndInfoButtonContainer>
              </QuestionsBoxContainer>
            </TimerAndQuestionsContainer>
          )}
        </QuestionAndOptionsContainer>
      )}
    </TestContainer>
  );
}
